export type SubscriptionPlanType =
  | "Base"
  | "Core"
  | "Program"
  | "HighSchools"
  | "HighSchoolsProgram"
  | "HyroxT0"
  | "HyroxT1"
  | "HyroxT2";

export const SubscriptionStatus = {
  Active: "Active",
  PastDue: "PastDue",
  Unpaid: "Unpaid",
  Trialing: "Trialing",
} as const;

export type SubscriptionStatusType = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export const SubscriptionInterval = {
  Monthly: "Monthly",
  Yearly: "Yearly",
} as const;

export type SubscriptionIntervalType = (typeof SubscriptionInterval)[keyof typeof SubscriptionInterval];

export interface SubscriptionDetails {
  currency: "USD" | "EUR" | "GBP";
  /**
   * The end of the current subscription interval, e.g., the approximate next billing date.
   */
  currentPeriodEndDate: string;
  intervalType: SubscriptionIntervalType;
  planType: SubscriptionPlanType;
  /**
   * The date at which the subscription was created and started.
   */
  startDate: string;
  /**
   * Describes the status of the Stripe subscription.
   * Only a subset of values are currently supported as outlined in constraints.
   * In the future, Trialing and Paused will be supported when trials are implemented.
   * - Canceled should never be used, as activeSubscriptionDetails will be null in that case.
   * - Incomplete & IncompleteExpired should rarely ever appear - this happens when the first payment is incomplete.
   */
  status: SubscriptionStatusType;
  stripeSubscriptionId: string;
  /**
   * Indicates whether there is a card payment method that can be used for this subscription.
   * Other payment methods do not effect this field.
   * This looks at both the subscription card payment methods and the customers default card payment methods.
   */
  hasCardPaymentMethod: boolean;
  /**
   * The date at which the subscription trial ends.
   * This is null if there has been no trial.
   * This can be a date in the past and is not cleared when a subscription status becomes Active.
   */
  trialEndDate: string | null;
}

export interface Organisation {
  id: string;
  name: string;
  email: string;
  profilePhotoName: string | null;
  stripeCustomerId: string | null;
  accountInfo: {
    athleteLimit: number;
    activeAthleteCount: number;
    activeCoachCount: number;
    archivedAthleteCount: number;
    coachLimit: number;
  };
  featureConfig: {
    isHubAthleteManagementEnabled: boolean;
    isCustomExercisesEnabled: boolean;
    isGroupsEnabled: boolean;
    isHubChartingEnabled: boolean;
    isHubDashboardsEnabled: boolean;
    isHubLeaderboardsEnabled: boolean;
    isHubLiveEnabled: boolean;
    isProgrammingEnabled: boolean;
    isWorkoutsEnabled: boolean;
    isSupportEnabled: boolean;
    isHyroxConditioningScoreEnabled: boolean;
  };
  subscriptionEndDate?: string | null;
  subscriptionDetails?: SubscriptionDetails;
}

export enum OrganisationRole {
  OWNER = "OWNER",
  COACH = "COACH",
  ATHLETE = "ATHLETE",
}
